import { unionWith } from "lodash";

import { mapToAppointmentModel } from "@teamprevent/common/calendar";
import { FixedLengthChildAppointment } from "@teamprevent/common/calendar/models";

import { BookIntoClinicStarted, BookIntoClinicError, BookIntoClinicSuccess, RebookAppointmentSuccess } from "../../bookAppointments/actions";
import { BOOK_INTO_CLINIC_STARTED, BOOK_INTO_CLINIC_ERROR, BOOK_INTO_CLINIC_SUCCESS, REBOOK_APPOINTMENT_SUCCESS } from "../../bookAppointments/actionTypes";
import { BookIntoClinicPayload } from "../../bookAppointments/models";
import { HealthSurveillanceAction } from "../../healthSurveillance/actions";
import {
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS,
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS
} from "../../healthSurveillance/actionTypes";
import { DELETE_APPOINTMENT_SUCCESS } from "../actionTypes";
import { CalendarAction } from "../actions";

import * as actionTypes from "./actionTypes";
import { ClinicAction } from "./actions";
import { ClinicDetails, ClinicState, MobileClinicDetails } from "./models";

type Action =
    | ClinicAction
    | BookIntoClinicStarted
    | BookIntoClinicError
    | BookIntoClinicSuccess
    | RebookAppointmentSuccess
    | HealthSurveillanceAction
    | CalendarAction;

export const clinics = (state: ClinicState = { clinics: [] }, action: Action): ClinicState => {
    switch (action.type) {
        case actionTypes.LOAD_CLINICS_SUCCESS:
            const loadedClinics = action.payload.map(mapToAppointmentModel);
            return { ...state, clinics: unionWith(loadedClinics, state.clinics, (a, b) => a.id === b.id) };
        case actionTypes.LOAD_CLINIC_SUCCESS:
        case actionTypes.EDIT_CLINIC_SUCCESS:
        case actionTypes.BLOCK_CLINIC_SUCCESS:
        case actionTypes.ADD_RECALL_CASES_TO_MOBILE_CLINIC_SUCCESS:
            const loadedClinic = mapToAppointmentModel(action.payload);
            return { ...state, clinics: [...state.clinics.filter((a) => a.id !== loadedClinic.id), loadedClinic] };
        case actionTypes.ADD_RECALL_CASE_TO_MOBILE_CLINIC_SUCCESS: {
            const clinic = state.clinics.find((a) => a.id === action.payload.clinicId) as MobileClinicDetails;
            if (clinic === undefined) { return state; }

            const editedClinic: MobileClinicDetails = {
                ...clinic,
                appointments: [...clinic.appointments, action.payload.appointment]
            };
            return { clinics: updateClinics(state.clinics, editedClinic) };
        }
        case actionTypes.CREATE_CLINIC_SUCCESS:
            const createdClinic = mapToAppointmentModel(action.payload);
            return { clinics: [...state.clinics, createdClinic] };
        case actionTypes.EDIT_CLINIC_CLINICIANS_SUCCESS:
            const editedClinic = mapToAppointmentModel(action.payload);
            return { clinics: updateClinics(state.clinics, editedClinic) };
        case actionTypes.CREATE_CLINIC_ERROR:
        case actionTypes.EDIT_CLINIC_CLINICIANS_ERROR:
            return { ...state, clinicErrors: action.payload };
        case BOOK_INTO_CLINIC_STARTED:
            return { ...state, clinics: state.clinics.map(clinic => updateAfterSuccessfulBooking(clinic, action.payload)) };
        case BOOK_INTO_CLINIC_ERROR:
            return { ...state, clinics: state.clinics.map(clinic => updateAfterErroredBooking(clinic, action.payload)) };
        case BOOK_INTO_CLINIC_SUCCESS:
            const updatedClinic = mapToAppointmentModel(action.payload.clinic);
            return { ...state, clinics: updateClinics(state.clinics, updatedClinic) };
        case DELETE_APPOINTMENT_SUCCESS:
            if (action.payload.clinicId) {
                return {
                    clinics: state.clinics.map(
                        c => c.id === action.payload.clinicId
                            ? {
                                ...c,
                                appointments: c.appointments.filter(a => a.id !== action.payload.appointmentId) as FixedLengthChildAppointment[]
                            }
                            : c)
                };
            } else {
                return { clinics: state.clinics.filter(c => c.id !== action.payload.appointmentId) };
            }
        case REBOOK_APPOINTMENT_SUCCESS:
            return { clinics: state.clinics.map(c => updateAfterCancellingAppointment(c, action.payload.cancelledAppointmentId)) };
        case SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS:
        case SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS:
            return {
                clinics: state.clinics.map(c => {
                    if (c.id === action.payload.clinicId) {
                        return {
                            ...c,
                            appointments: (c.appointments as FixedLengthChildAppointment[]).map(a => {
                                if (a.id === action.payload.appointmentId) {
                                    return {
                                        ...a,
                                        isClinicalNotesComplete: true
                                    };
                                }

                                return a;
                            })
                        };
                    }

                    return c;
                })
            };
        default:
            return state;
    }
};

const updateAfterSuccessfulBooking = (clinic: ClinicDetails, payload: BookIntoClinicPayload): ClinicDetails => {
    if (clinic.id === payload.appointment.parentAppointment?.id) {
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        const { parentAppointment, ...newAppointment } = payload.appointment;
        newAppointment.customer = payload.bookTask.customer;
        newAppointment.employee = payload.bookTask.employee;
        return { ...clinic, appointments: [...clinic.appointments, newAppointment] } as ClinicDetails;
    }
    return clinic;
};

const updateAfterErroredBooking = (clinic: ClinicDetails, payload: BookIntoClinicPayload): ClinicDetails => {
    if (clinic.id === payload.appointment.parentAppointment?.id) {
        return { ...clinic, appointments: clinic.appointments.filter(appointment => !appointment.start.isSame(payload.appointment.start)) } as ClinicDetails;
    }
    return clinic;
};

const updateAfterCancellingAppointment = (clinic: ClinicDetails, cancelledAppointmentId: string): ClinicDetails => ({
    ...clinic,
    appointments: clinic.appointments.filter(a => a.id !== cancelledAppointmentId) as FixedLengthChildAppointment[]
});

const updateClinics = (existingClinics: ClinicDetails[], payload: ClinicDetails): ClinicDetails[] => {
    return existingClinics.map(c => c.id === payload.id ? payload : c);
};
